import type { CustomerDto } from '@tempo-io/fim3-dto';
import { getInitials } from '@tempo-io/string-util';
import { Avatar, AvatarFallback, AvatarImage } from '@tempo-io/sui-avatar';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { Close } from '@tempo-io/sui-icons';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerHeaderDisplayValue } from '@/common/api/types/loopVisualization';

export type CustomerDisplayValue = Omit<CustomerHeaderDisplayValue, 'type'>;

type SwimlaneCustomerChipProps = {
  customer: CustomerDisplayValue;
  className?: string;
  compact?: boolean;
};

interface CustomerFieldDisplayProps {
  customerId: number;
  customer?: CustomerDto;
  isLoading?: boolean;
  isEditable: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  isDisabled?: boolean;
  placeholder?: string;
}

export function SwimlaneCustomerChip({
  customer,
  className,
  compact = false,
}: SwimlaneCustomerChipProps) {
  const initials = getInitials(customer.name) || '?';

  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5', className)}>
      <Avatar size="sm" className={cn('shrink-0', compact ? 'size-4' : 'size-5')}>
        <AvatarImage src={customer.logoUrl ?? undefined} alt={customer.name} />
        <AvatarFallback className="text-[0.625rem]">{initials}</AvatarFallback>
      </Avatar>
      <span className="truncate">{customer.name}</span>
    </span>
  );
}

export function CustomerFieldDisplay(props: CustomerFieldDisplayProps): ReactElement {
  const { t } = useTranslation();

  const {
    customerId,
    customer,
    isLoading = false,
    isEditable,
    onClick,
    onRemove,
    isDisabled = false,
    placeholder,
  } = props;

  const isArchived = !!customer?.archivedAt;
  const isDeleted = !!customer?.deletedAt;
  const isMuted = isArchived || isDeleted;

  const displayName = customer
    ? customer.name
    : isLoading
      ? ''
      : t(($) => $.adaptivePlanner.itemPanel.customerField.unknownCustomer);

  const lifecycleLabel = isDeleted
    ? t(($) => $.adaptivePlanner.itemPanel.customerField.deleted)
    : isArchived
      ? t(($) => $.adaptivePlanner.itemPanel.customerField.archived)
      : null;

  const isClickable = isEditable && !isDisabled && !!onClick;

  if (!isEditable) {
    return (
      <span
        data-testid={`customer-field-display-${customerId}`}
        className={cn('text-sm', isMuted && 'text-text-neutral-secondary')}
      >
        {displayName}
        {lifecycleLabel && <span className="ml-1 text-xs">({lifecycleLabel})</span>}
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`customer-field-display-${customerId}`}
        onClick={onClick}
        disabled={!isClickable}
        className={cn(
          'border-border-neutral bg-bg-surface-base flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors w-full',
          isClickable && 'hover:bg-bg-neutral-subtle cursor-pointer',
          !isClickable && 'cursor-default',
          isMuted && 'text-text-neutral-secondary',
          customer && onRemove && !isDisabled && 'pr-10',
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="truncate">
            {displayName ||
              placeholder ||
              t(($) => $.adaptivePlanner.itemPanel.customerField.selectCustomer)}
          </span>
          {lifecycleLabel && (
            <span className="text-xs text-text-neutral-secondary shrink-0">({lifecycleLabel})</span>
          )}
        </div>
      </button>
      {customer && onRemove && !isDisabled && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-1/2 -translate-y-1/2 size-6 p-0 hover:bg-bg-surface-base z-10"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={t(($) => $.adaptivePlanner.itemPanel.customerField.clear)}
        >
          <Close size={16} />
        </Button>
      )}
    </div>
  );
}
